// `GET /api/v1/me/access` (J-034): the caller's ACO module access and the server's idle timeout
// (`SESSION_IDLE_MINUTES`, D-989). The response carries no PHI; TanStack Query's cache is cleared on
// sign-out with everything else. J-238: it also says whether the caller sees only its granted groups.
// Relative imports, so the root Vitest project, which does not resolve `@/`, can test this file.
import { type QueryClient, queryOptions } from "@tanstack/react-query";
import { groupGrantSchema, myAccessSchema, type MyAccess } from "@vbltc/schema";
import { z } from "zod";
import { fetchClient } from "../../../api/client";
import { ApiReadError } from "../../../api/resilient-fetch";
import { withGroupScope } from "../../../lib/group-access";

/**
 * The answer an older server sends, without the J-238 scope fields, still passes. J-278: so does a group
 * without `combinedFiguresAllowed`; an older server sends only a group user's grants, which may always
 * show their combined figures. J-383: so does an answer without `gapClosureWorkbook`; an older server has no
 * such switch, so its Gap Closure Workbook is on.
 */
const wireAccessSchema = myAccessSchema
  .extend({
    groups: z.array(
      groupGrantSchema.extend({ combinedFiguresAllowed: z.boolean().optional().default(true) }),
    ),
    gapClosureWorkbook: z.boolean().optional().default(true),
  })
  .partial({ scopeKind: true, groups: true });

export const meAccessQueryOptions = () =>
  queryOptions({
    queryKey: ["auth", "me-access"],
    queryFn: async () => {
      const { data, error, response } = await fetchClient.GET("/api/v1/me/access");
      // J-266: an error answer, or a 200 whose body is empty or not the caller's access, is a failed
      // read. Read as an ACO-set user's access, it would open ACO-wide screens to a group user.
      const access = wireAccessSchema.safeParse(data);
      if (error || !access.success) throw new ApiReadError(response.status, error?.error);
      // J-238: `scopeKind` and `groups`, with an older server's answer read as an ACO-set user's.
      return withGroupScope(access.data);
    },
    // J-266: a failed read is asked again by Retry only, as metadata's is (J-215). With TanStack's
    // default, Settings, Users & Access and a bundle's load page, which each observe this read, sent it
    // again as they mounted; that put the layout's scope gate back to "checking" and unmounted them,
    // which is the "Loading." without end the test session found.
    retryOnMount: false,
    staleTime: 5 * 60 * 1000,
  });

/**
 * J-238: the caller's access for a route guard, from the cache when it holds it; undefined when the read
 * fails, so a guard falls back to the role (the server refuses what the scope may not open either way).
 */
export const readMyAccess = (queryClient: QueryClient): Promise<MyAccess | undefined> =>
  queryClient.ensureQueryData(meAccessQueryOptions()).catch(() => undefined);
